import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import * as FaIcons from "react-icons/fa";
import {
  FaHeadset,
  FaChartLine,
  FaShieldAlt,
  FaCheckCircle,
  FaArrowRight,
  FaPhoneAlt,
} from "react-icons/fa";

import "./Services.css";
import { BASE_URL } from "../../Config/config";

const benefits = [
  {
    icon: <FaCheckCircle />,
    title: "Professional Implementation",
    description:
      "Get your software properly configured and implemented by experienced professionals.",
  },
  {
    icon: <FaHeadset />,
    title: "Reliable Support",
    description:
      "Our support team is available to help you solve software and technical issues.",
  },
  {
    icon: <FaChartLine />,
    title: "Business Growth",
    description:
      "Use technology to improve productivity, reduce manual work and grow your business.",
  },
  {
    icon: <FaShieldAlt />,
    title: "Secure Solutions",
    description:
      "We focus on dependable software, data protection and reliable business operations.",
  },
];
const DEFAULT_SERVICE_ICON = "FaTools";

const DynamicServiceIcon = ({ name, ...props }) => {
  const iconName = String(name || DEFAULT_SERVICE_ICON).trim();

  const IconComponent = FaIcons[iconName] || FaIcons[DEFAULT_SERVICE_ICON];

  return <IconComponent {...props} />;
};
export default function Services() {
  const [services, setServices] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out-cubic",
      once: false,
      offset: 80,
    });
  }, []);

  // your existing fetch useEffect...

  // =====================================================
  // FETCH SERVICES
  // =====================================================

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);

        const response = await fetch(`${BASE_URL}/services.php?action=get`);

        if (!response.ok) {
          throw new Error(`Services API returned ${response.status}`);
        }

        const result = await response.json();

        if (!result.status) {
          throw new Error(result.message || "Failed to fetch services");
        }

        const apiServices = Array.isArray(result.data) ? result.data : [];

        const formattedServices = apiServices.map((service) => ({
          ...service,

          icon: service.icon || DEFAULT_SERVICE_ICON,

          title: service.title || "",

          description: service.description || "",

          features: Array.isArray(service.features) ? service.features : [],

          display_order: Number(service.display_order) || 1,
        }));

        formattedServices.sort((a, b) => a.display_order - b.display_order);

        setServices(formattedServices);
      } catch (error) {
        console.error("Fetch services error:", error);

        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return (
    <main className="services-page">
      {/* <section
        className="contact-hero"
        data-aos="fade-down"
        data-aos-duration="1000"
      >
        <div className="contact-hero-overlay"></div>

        <div
          className="section-container contact-hero-content"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          <span className="section-label">OUR SERVICES</span>

          <h1>
            Technology Solutions
            <span> For Your Business</span>
          </h1>

          <p>
            From accounting and ERP software to HRMS, custom software
            development and technical support, Star Computers provides complete
            technology solutions to help your business work smarter.
          </p>

          <div
            className="services-hero-buttons"
            data-aos="fade-up"
            data-aos-delay="400"
          >
            <a href="#services" className="services-primary-btn">
              Explore Services
              <FaArrowRight />
            </a>

            <a href="#contact" className="services-secondary-btn">
              <FaPhoneAlt />
              Talk to an Expert
            </a>
          </div>
        </div>
      </section> */}

      {/* =====================================================
          SERVICES
      ===================================================== */}

      <section className="services-section" id="services">
        <div className="section-container">
          <div className="section-heading services-heading" data-aos="fade-up">
            <span className="section-label">WHAT WE DO</span>

            <h2>
              Complete Solutions For
              <span> Your Business</span>
            </h2>

            <p>
              We provide practical and reliable software solutions that simplify
              business operations and help organizations improve productivity.
            </p>
          </div>

          {/* =====================================================
              LOADING
          ===================================================== */}

          {loading && (
            <div className="services-loading">
              <p>Loading services...</p>
            </div>
          )}

          {/* =====================================================
              ERROR
          ===================================================== */}

          {!loading && error && (
            <div className="services-error">
              <p>{error}</p>
            </div>
          )}

          {/* =====================================================
              EMPTY
          ===================================================== */}

          {!loading && !error && services.length === 0 && (
            <div className="services-empty">
              <p>No services available at the moment.</p>
            </div>
          )}

          {/* =====================================================
              SERVICE GRID
          ===================================================== */}

          {!loading && !error && services.length > 0 && (
            <div className="services-grid">
              {services.map((service, index) => {
                const features = Array.isArray(service.features)
                  ? service.features
                  : [];

                return (
                  <div
                    className="service-card"
                    key={service.id}
                    data-aos="fade-up"
                    data-aos-delay={100 + (index % 4) * 100}
                    data-aos-duration="700"
                  >
                    <div className="service-card-top">
                      <div
                        className="service-icon"
                        data-aos="zoom-in"
                        data-aos-delay={150 + (index % 4) * 100}
                      >
                        <DynamicServiceIcon name={service.icon} />
                      </div>
                      <span className="service-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>

                    <h3>{service.title}</h3>

                    <p className="service-description">{service.description}</p>

                    <div className="service-features">
                      {features.map((feature, featureIndex) => (
                        <div className="service-feature" key={featureIndex}>
                          <FaCheckCircle />

                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <a href="#contact" className="service-link">
                      Get Started
                      <FaArrowRight />
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* =====================================================
          WHY OUR SERVICES
      ===================================================== */}

      <section className="services-benefits" data-aos="fade-up">
        <div className="section-container">
          <div className="benefits-grid">
            <div className="benefits-content" data-aos="fade-right">
              <span className="section-label">WHY CHOOSE US</span>

              <h2>
                More Than Software.
                <span> We Provide Solutions.</span>
              </h2>

              <p>
                Choosing the right software is only the beginning. Our team
                helps you implement, configure, maintain and optimize your
                technology so it continues to support your business as it grows.
              </p>

              <a href="#contact" className="benefits-button">
                Talk to Our Experts
                <FaArrowRight />
              </a>
            </div>

            <div
              className="benefits-cards"
              data-aos="fade-left"
              data-aos-delay="200"
            >
              {benefits.map((benefit, index) => (
                <div
                  className="benefit-card"
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                >
                  <div className="benefit-icon">{benefit.icon}</div>

                  <div>
                    <h3>{benefit.title}</h3>

                    <p>{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          PROCESS
      ===================================================== */}

      <section className="service-process">
        <div className="section-container">
          <div className="section-heading" data-aos="fade-up">
            <span className="section-label">OUR PROCESS</span>

            <h2>
              Simple Process.
              <span> Better Results.</span>
            </h2>

            <p>
              We follow a simple and transparent approach to understand your
              business and provide the right technology solution.
            </p>
          </div>

          <div className="process-grid" data-aos="fade-up" data-aos-delay="200">
            <div className="process-item">
              <div className="process-number">01</div>

              <h3>Understand</h3>

              <p>
                We understand your business requirements, challenges and
                existing processes.
              </p>
            </div>

            <div className="process-item">
              <div className="process-number">02</div>

              <h3>Recommend</h3>

              <p>
                Our experts recommend the software and solution that best fits
                your business.
              </p>
            </div>

            <div className="process-item">
              <div className="process-number">03</div>

              <h3>Implement</h3>

              <p>
                We configure and implement the solution according to your
                business requirements.
              </p>
            </div>

            <div className="process-item">
              <div className="process-number">04</div>

              <h3>Support</h3>

              <p>
                Our team continues to provide technical support and assistance
                whenever you need it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      {/* <section className="services-cta">
        <div className="section-container">
          <div className="services-cta-content">
            <div>
              <span>NEED THE RIGHT SOFTWARE?</span>

              <h2>
                Let's Find the Right Solution
                <br />
                For Your Business
              </h2>

              <p>
                Talk to Star Computers experts today and discover how technology
                can simplify your business.
              </p>
            </div>

            <a href="#contact" className="services-cta-button">
              Contact Us
              <FaArrowRight />
            </a>
          </div>
        </div>
      </section> */}
    </main>
  );
}
